import { getRecommendationSections } from './recommendationSections';

export function buildLegacySummary(description = '') {
    if (!description) return '';
    const split = description.split('**Analyse**');
    return normalizeQuickViewDescription(split[0]);
}

const SUMMARY_MAX_CHARS = 400;
const SUMMARY_MIN_TARGET_CHARS = 280;
const SOFT_STOP_WORDS = new Set([
    'a',
    'au',
    'aux',
    'avec',
    'ce',
    'ces',
    'de',
    'des',
    'du',
    'en',
    'et',
    'la',
    'le',
    'les',
    'ou',
    'pour',
    'que',
    'qui',
    'un',
    'une',
    'the',
    'of',
    'or',
    'and',
    'with',
    'for',
    'to',
]);

function normalizeQuickViewDescription(value = '') {
    const text = String(value || '')
        .replace(/\s+/g, ' ')
        .trim();

    if (!text) return '';

    const withoutEllipsis = text.replace(/(?:\.\.\.|\u2026)+\s*$/u, '').trim();
    if (!withoutEllipsis) return '';

    const sentences = withoutEllipsis.match(/[^.!?]+[.!?]/g) || [];
    if (sentences.length > 0) {
        let compact = '';
        for (const sentence of sentences) {
            const next = `${compact} ${sentence}`.trim();
            if (next.length > SUMMARY_MAX_CHARS) break;
            compact = next;
            if (compact.length >= SUMMARY_MIN_TARGET_CHARS) break;
        }
        if (compact) return compact;
    }

    return smartPreview(withoutEllipsis, SUMMARY_MAX_CHARS);
}

function smartPreview(value = '', limit = SUMMARY_MAX_CHARS) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    if (text.length <= limit) {
        if (/[.!?]$/.test(text)) return text;
        return text.length < limit ? `${text}.` : `${text.slice(0, limit - 1).trimEnd()}.`;
    }

    const slice = text.slice(0, limit);
    const punctuationIndex = Math.max(
        slice.lastIndexOf('.'),
        slice.lastIndexOf('!'),
        slice.lastIndexOf('?'),
        slice.lastIndexOf(';')
    );

    if (punctuationIndex >= Math.min(160, Math.floor(limit * 0.45))) {
        return slice.slice(0, punctuationIndex + 1).trim();
    }

    let clipped = slice.slice(0, Math.max(0, limit - 3)).trim();
    if (clipped.includes(' ')) {
        clipped = clipped.replace(/\s+\S*$/, '').trim();
    }

    clipped = clipped.replace(/[,:;.\s]+$/u, '').trim();
    const words = clipped.split(/\s+/);
    while (words.length > 1 && SOFT_STOP_WORDS.has(words[words.length - 1].toLowerCase())) {
        words.pop();
    }

    const clean = words.join(' ').replace(/[,:;.\s]+$/u, '').trim();
    return clean ? `${clean}...` : '';
}

export function buildDetailedMarkdownFromStructured(detailedView, t, roleLabel = null) {
    if (!detailedView || typeof detailedView !== 'object') return '';

    const legalQualification = detailedView.legal_qualification || '';
    const problem = detailedView.problem || '';
    const impact = detailedView.impact || '';
    const roleRisk = Array.isArray(detailedView.role_risk) ? detailedView.role_risk : [];
    const recommendationSections = getRecommendationSections(detailedView.recommendation);
    const justification = detailedView.justification || '';

    const roleSectionTitle = roleLabel || t('ai_comment_card.role_risk', 'Risque pour le role');

    const parts = [];

    if (problem) {
        parts.push(`**${t('ai_comment_card.problem', 'Probleme')} :** ${problem}`);
    }

    if (impact) {
        parts.push(`**${t('ai_comment_card.impact', 'Impact')} :** ${impact}`);
    }

    parts.push(`**${roleSectionTitle}**`);

    if (roleRisk.length > 0) {
        parts.push(roleRisk.map((item) => `- ${item}`).join('\n'));
    } else {
        parts.push(`- ${t('ai_comment_card.no_role_risk', 'Risque non precise.')}`);
    }

    parts.push(`**${t('ai_comment_card.recommendation', 'Piste de traitement')}**`);

    const recommendationLabels = {
        primary: t('ai_comment_card.primary_action', 'Levier prioritaire à examiner'),
        drafting_target: t('ai_comment_card.drafting_target', 'Exemple de formulation à valider'),
        alternative: t('ai_comment_card.alternative_version', 'Option alternative à examiner'),
    };

    recommendationSections.forEach((section) => {
        parts.push(`- **${recommendationLabels[section.key] || recommendationLabels.alternative} :** ${section.text}`);
    });

    if (justification) {
        parts.push(`**${t('ai_comment_card.justification', 'Justification')} :** ${justification}`);
    }

    return parts.filter(Boolean).join('\n\n');
}

export function getCommentSummaryMarkdown(comment, t) {
    const quickView = comment?.quick_view || comment?.quickView || null;

    if (quickView && typeof quickView === 'object' && quickView.description) {
        const description = normalizeQuickViewDescription(quickView.description);
        const action = String(quickView.action || '').trim();

        return [
            description,
            action
                ? `**${t('ai_comment_card.action_label', 'Levier recommandé')}** - ${action}`
                : null,
        ]
            .filter(Boolean)
            .join('\n\n');
    }

    if (quickView && typeof quickView === 'object' && (quickView.risk || quickView.action)) {
        return [
            quickView?.risk
                ? `**${t('ai_comment_card.risk_label', 'Risque identifie')}** - ${quickView.risk}`
                : null,
            '',
            quickView?.action
                ? `**${t('ai_comment_card.action_label', 'Levier recommandé')}** - ${quickView.action}`
                : null,
        ]
            .filter(Boolean)
            .join('\n\n');
    }

    return buildLegacySummary(comment?.description || '');
}

export function getCommentExpandedMarkdown(comment, t) {
    const detailedView = comment?.detailed_view || comment?.detailedView || null;

    if (detailedView && typeof detailedView === 'object') {
        return buildDetailedMarkdownFromStructured(
            detailedView,
            t,
            t('ai_comment_card.role_risk', 'Risque pour le role')
        );
    }

    return comment?.description || '';
}
